import { useCallback, useContext, useMemo, useState } from 'react';

import { ServiceFormTemplate, Step } from '@/widgets/ServiceFormTemplate';

import {
  WeaponPlaceOfResidence,
  PlaceOfResidence,
  SignUp,
  weaponPlaceOfResidenceBaseSchema,
  placeOfResidenceSchema,
  multipleWeaponSelectionSchema,
  MultipleWeaponSelection,
} from '@/features/ui';
import { AdditionalDocuments } from '@/features/ui/Steps/AdditionalDocuments';
import { additionalDocumentsSchema } from '@/features/ui/Steps/AdditionalDocuments/model/schema';
import { PersonalAndPassportData, personalAndPassportDataSchema } from '@/features/ui/Steps/PersonalAndPassportData';

import { useFormDraftPlainTools } from '@/hooks/index';
import { useAsyncDataLoader } from '@/hooks/useAsyncDataLoader';

import { ServiceFileManager } from '@/helpers/serviceFileManager';

import { ServicesContext } from '@/contexts/Services/ServicesContext';

import { FormDataType, UserRequestDraft, UserService } from '@/api/types';
import { SERVICE_CODES } from '@/lib/consts';

import api from '@/api';

import {
  disallowedDocumentTypeCodesForAdditionalDocumentStep,
  SERVICE_CODE,
  STEP_NUMBER_SIGN_UP,
} from './lib/constants';
import { generatePayload } from './lib/generatePayload';
import { ServiceProps } from './model/types';
import { DataPreview } from './ui/DataPreview';

import './ResidenceRelocation.scss';
import { ResidenceRelocationResult } from './ui/ResidenceRelocationResult';

type ResidenceRelocationServiceCode = typeof SERVICE_CODES.RESIDENCE_RELOCATION;

interface Props {
  serviceCode: ResidenceRelocationServiceCode;
}

export const STEPS: Array<Step<ServiceProps>> = [
  {
    title: 'Вибір зброї',
    step: MultipleWeaponSelection,
    validationSchema: multipleWeaponSelectionSchema,
  },
  {
    title: 'Особисті та паспортні дані',
    step: PersonalAndPassportData,
    validationSchema: personalAndPassportDataSchema,
  },
  {
    title: 'Місце проживання',
    step: PlaceOfResidence,
    validationSchema: placeOfResidenceSchema,
  },
  {
    title: 'Нова адреса місця зберігання зброї',
    step: WeaponPlaceOfResidence,
    validationSchema: weaponPlaceOfResidenceBaseSchema,
  },
  {
    title: 'Додаткові дані',
    step: AdditionalDocuments,
    validationSchema: additionalDocumentsSchema,
  },
  {
    title: 'Підтвердження даних',
    step: DataPreview,
  },
  {
    title: 'Електронний Підпис (КЕП)',
    step: SignUp,
  },
  {
    title: 'Результат',
    step: ResidenceRelocationResult,
  },
];

export const ResidenceRelocation: React.FC<Props> = ({ serviceCode }) => {
  const { getServiceByCode } = useContext(ServicesContext);
  const currentService = getServiceByCode(SERVICE_CODE) as UserService;
  const serviceFileManager = useMemo(() => new ServiceFileManager(), []);
  const [defaultValues, setDefaultValues] = useState<FormDataType>({});
  const {
    isLoading: isLoadingDraft,
    onDraftContinue,
    onDraftSave,
    onDraftCreate,
    currentRequestId,
  } = useFormDraftPlainTools({
    serviceCode: SERVICE_CODE,
    setData: (data) => {
      setDefaultValues(data ?? {});
    },
    stepNumberToSignUp: STEP_NUMBER_SIGN_UP,
  });

  const [isLoadingPdf, setIsLoadingPdf] = useState(false);

  const { load: submitForm, isLoading: isLoadingSubmit } = useAsyncDataLoader(api.services.submit);

  const handleDraftCreate = async (_: any, specificStep?: (step: number) => void): Promise<Nullable<string>> =>
    specificStep &&
    onDraftCreate(
      {
        id: currentService.id,
        name: currentService.name,
        code: SERVICE_CODE,
        totalSteps: STEPS.length,
        currentStep: 1,
        lastStepFilled: 1,
      },
      specificStep,
    );

  const handleGeneratePDF = useCallback(
    async (formData: FormDataType): Promise<void> => {
      const { commonPayload } = await generatePayload({
        requestId: (currentRequestId ?? '') as UserRequestDraft['id'],
        formData,
        service: currentService,
      });

      setIsLoadingPdf(true);
      try {
        const result = await api.services.generatePdf(SERVICE_CODE, commonPayload);
        serviceFileManager.setAttachment(result);
      } catch (error) {
        throw error;
      } finally {
        setIsLoadingPdf(false);
      }
    },
    [currentRequestId, currentService, serviceFileManager],
  );

  const handleSubmitForm = useCallback(
    async (formData: FormDataType): Promise<void> => {
      const { signedPayload } = await generatePayload({
        requestId: (currentRequestId ?? '') as UserRequestDraft['id'],
        formData,
        service: currentService,
        serviceFileManager,
      });

      await submitForm(serviceCode, signedPayload);

      serviceFileManager.download();
      serviceFileManager.clear();
    },
    [currentRequestId, currentService, serviceFileManager, submitForm, serviceCode],
  );

  const isLoading = isLoadingDraft || isLoadingPdf || isLoadingSubmit;

  return (
    <ServiceFormTemplate
      className="residence-relocation"
      serviceCode={SERVICE_CODE}
      title={currentService.name}
      steps={STEPS}
      isLoading={isLoading}
      defaultValues={defaultValues}
      skipAvailabilityValidation={(step) => step === 2}
      currentRequestId={currentRequestId ?? null}
      onDraftContinue={onDraftContinue}
      onDraftSave={onDraftSave}
      onDraftCreate={handleDraftCreate}
      stepProps={{
        handleGeneratePDF,
        serviceFileManager,
        submitForm: handleSubmitForm,
        showNpuPaymentInformer: false,
        shouldBeAbleToUpdateProfile: currentService.sync_profile_available ?? false,
        disallowedDocumentTypeCodes: disallowedDocumentTypeCodesForAdditionalDocumentStep,
      }}
    />
  );
};
